import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // login, register, wholesale

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    localStorage.setItem('token', response.data.access_token);
    setUser(response.data.user);
    setShowAuthModal(false);
    return response.data;
  };

  const register = async (name, email, password, accountType = 'retail') => {
    const response = await axios.post(`${API_URL}/api/auth/register`,
      { name, email, password, account_type: accountType },
      { withCredentials: true }
    );
    localStorage.setItem('token', response.data.access_token);
    setUser(response.data.user);
    setShowAuthModal(false);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // ignore
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  const openAuthModal = (mode = 'login') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  // Google OAuth login - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/session`, {}, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true
      });
      localStorage.setItem('token', response.data.session_token);
      setUser({
        user_id: response.data.user_id,
        email: response.data.email,
        name: response.data.name,
        account_type: response.data.account_type,
        is_admin: response.data.is_admin
      });
      return response.data;
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    handleOAuthCallback,
    showAuthModal,
    authMode,
    openAuthModal,
    closeAuthModal,
    setAuthMode,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    isWholesale: user?.account_type === 'wholesale' && user?.is_wholesale_approved
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
